import { useLocation } from "react-router-dom";
import ButtonComponent from "../shared/button/button.component";
import { useEffect, useMemo, useState } from "react";
import { Document, Page } from 'react-pdf';
import { useSelector } from "react-redux";
import * as selector from "../../selectors/selectors";
import { FalconLogo } from "../../icon";

const base64ToUint8Array = (base64: string): Uint8Array => {
    try {
        let cleanBase64 = base64.replace(/\s/g, '');
        if (cleanBase64.includes('data:')) {
            const base64Index = cleanBase64.indexOf('base64,');
            if (base64Index !== -1) {
                cleanBase64 = cleanBase64.substring(base64Index + 7);
            } else {
                const commaIndex = cleanBase64.indexOf(',');
                if (commaIndex !== -1) {
                    cleanBase64 = cleanBase64.substring(commaIndex + 1);
                }
            }
        }
        
        if (!/^[A-Za-z0-9+/]*={0,3}$/.test(cleanBase64)) {
            cleanBase64 = cleanBase64.replace(/[^A-Za-z0-9+/=]/g, '');
        }
        
        while (cleanBase64.length % 4 !== 0) {
            cleanBase64 += '=';
        }
        
        const binaryString = atob(cleanBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        
        return bytes;
    } catch (error) {
        throw new Error(`Failed to decode base64 PDF data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
};

const CompletedPDFViewerComponent: React.FC = () => {
    const location = useLocation();
    const response = location.state?.response;
    const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const fileId = response?.data?.[0]?.file_id;
    const fileName = response?.data?.[0]?.file_name || 'document.pdf';
    const pdfBase64 = response?.data?.[0]?.value;
    const fileData = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);

    useEffect(() => {
        if (pdfBase64) {
            loadPDFData();
        }
    }, [pdfBase64]);

    const loadPDFData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            if (!pdfBase64 || typeof pdfBase64 !== 'string' || pdfBase64.length === 0) {
                throw new Error('No PDF data found in response');
            }

            const byteArray = base64ToUint8Array(pdfBase64);

            if (byteArray.length < 4) {
                throw new Error('PDF data too short to contain valid header');
            }
            
            let pdfHeaderString = '';
            for (let i = 0; i < 4; i++) {
                pdfHeaderString += String.fromCharCode(byteArray[i]);
            }
            
            if (pdfHeaderString !== '%PDF') {
                throw new Error(`Invalid PDF header. Expected '%PDF', got '${pdfHeaderString}' (bytes: ${Array.from(byteArray.slice(0, 4)).join(', ')})`);
            }

            setPdfData(byteArray);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to load PDF";
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const downloadPDF= () => {
        if (!pdfBase64 || !fileName) {
            return;
        }

        try {
            let cleanBase64 = pdfBase64.replace(/\s/g, '');
            if (cleanBase64.includes('data:')) {
                const base64Index = cleanBase64.indexOf('base64,');
                if (base64Index !== -1) {
                    cleanBase64 = cleanBase64.substring(base64Index + 7);
                }
            }
            const dataURL = `data:application/pdf;base64,${cleanBase64}`;
            const link = document.createElement('a');
            link.href = dataURL;
            link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            alert(`Failed to download PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, numPages));
    const goToFirstPage = () => setCurrentPage(1);
    const goToLastPage = () => setCurrentPage(numPages);

    const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const pageNum = parseInt(e.target.value);
        if (pageNum >= 1 && pageNum <= numPages) {
            setCurrentPage(pageNum);
        }
    };

    const getPdfWidth = () => {
        if (IsMobileDevice) {
            return window.innerWidth - 2; 
        }
        return 800; 
    };

    if (isLoading) {
        return (
        <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="success-page-text pt-3 pl-3 t-align-center">Loading PDF document...</p>
        </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-message">
                    <h3>Error Loading Document</h3>
                    <p>{error}</p>
                </div>
                <button onClick={loadPDFData} className="retry-button">
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="d-flex flex-direction h-v-100">

            <div className="doc-header bg-color-primary-background-pink d-flex justify-between align-center">
                <div className="doc-header-logo">
                    <FalconLogo />
                </div>
            
                <div className="flex-1 t-align-center">
                    <div className="m-0 color-primary-accent h4-bold ">{fileName}</div>
                </div>
            
                <div className="doc-header-right">
                    <ButtonComponent
                        OnClick={downloadPDF}
                        ButtonLabel="Download PDF"
                        ClassName="download-button"
                        Variant="primary-btn-small"
                    />
                </div>
            </div>

            <div className={`flex-1 o-y-auto o-x-auto flex-direction d-flex min-h-0 ${IsMobileDevice ? 'p-0' : 'p-3'}`}>
                {pdfData ? (
                    <div className="flex-1 flex-direction d-flex min-h-0">
                        <div className="pdf-navigation d-flex align-center justify-center bg-color-secondary h6-medium">
                            <button onClick={goToFirstPage} disabled={currentPage === 1} className="pdf-nav-button b-none h6-medium c-pointer">⏮️ First</button>
                            <button onClick={goToPreviousPage} disabled={currentPage === 1} className="pdf-nav-button b-none h6-medium c-pointer">⬅️ Previous</button>

                            <div className="pdf-page-input-wrapper d-flex align-center">
                                <span className="h6-medium color-primary-dark">Page</span>
                                <input
                                    type="number"
                                    className="pdf-page-input color-primary-dark h6-medium t-align-center"
                                    value={currentPage}
                                    onChange={handlePageInputChange}
                                    min={1}
                                    max={numPages}
                                />
                                <span className="h6-medium color-primary-dark">of {numPages}</span>
                            </div>

                            <button onClick={goToNextPage} disabled={currentPage === numPages} className="pdf-nav-button b-none h6-medium c-pointer">Next ➡️</button>
                            <button onClick={goToLastPage} disabled={currentPage === numPages} className="pdf-nav-button b-none h6-medium c-pointer">Last ⏭️</button>
                        </div>

                        <div className="pdf-document-wrapper d-flex justify-center p-relative ">
                            <Document
                                file={fileData}
                                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                                onLoadError={(error) => {
                                    setError('Failed to load PDF document');
                                }}
                            >
                                <Page 
                                    pageNumber={currentPage} 
                                    width={getPdfWidth()}
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                />
                            </Document>
                        </div>
                    </div>
                ) : (
                    <div className="h3-semibold t-align-center mt-2 color-primary-accent-alt">
                        No PDF data available
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompletedPDFViewerComponent;